/**
 * One message when the session runs out, instead of a workspace that quietly stops working.
 *
 * Deliberately **not** dismissible: there is nothing behind it that works. Escape, a click on the scrim
 * and a cancel button would all leave someone poking at a dead workspace. The one button hands them the
 * sign-in screen.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useT } from '../i18n'

export function SessionExpired({ onSignIn }: { onSignIn: () => void }) {
  const t = useT()
  const [leaving, setLeaving] = useState(false)

  // A dead session means dead streams. Make sure a stray keystroke aimed at the phone does not land on
  // the page underneath.
  useEffect(() => {
    const swallow = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') e.stopPropagation()
    }
    window.addEventListener('keydown', swallow, true)
    return () => window.removeEventListener('keydown', swallow, true)
  }, [])

  return createPortal(
    <div className="scrim" role="dialog" aria-modal="true" aria-label={t.session.expiredTitle}>
      <div className="dialog">
        <h3>{t.session.expiredTitle}</h3>
        <p className="dim">{t.session.expiredBody}</p>
        <div className="actions">
          <button
            className="primary"
            autoFocus
            disabled={leaving}
            onClick={() => {
              setLeaving(true)
              onSignIn()
            }}
          >
            {t.session.expiredOk}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
